import type { ClientConfigDto } from '@order-bot/shared-types';
import type * as SentryReact from '@sentry/react';
import type { Breadcrumb, ErrorEvent } from '@sentry/react';

import { env } from './env';

/**
 * Same masks as the API's (apps/api/src/common/monitoring/scrub.ts): retailers' phone numbers,
 * emails and tokens never leave the browser in an error report.
 */
const PATTERNS: Array<[RegExp, string]> = [
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]'],
  [/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, '[token]'],
  [/\bBearer\s+[\w.~+/-]+=*/gi, 'Bearer [token]'],
  [/\b[a-f0-9]{32,}\b/gi, '[secret]'],
  [/\+?\d[\d\s-]{8,14}\d/g, '[phone]'],
];

export const scrubText = (text: string): string =>
  PATTERNS.reduce((out, [pattern, mask]) => out.replace(pattern, mask), text);

export const scrubEvent = (event: ErrorEvent): ErrorEvent => {
  if (event.message) event.message = scrubText(event.message);
  for (const exception of event.exception?.values ?? []) {
    if (exception.value) exception.value = scrubText(exception.value);
  }
  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.query_string;
    delete event.request.headers;
    if (event.request.url) event.request.url = scrubText(event.request.url);
  }
  delete event.user;
  return event;
};

export const scrubBreadcrumb = (crumb: Breadcrumb): Breadcrumb | null => {
  // Console lines can print an order or a transcript: never kept.
  if (crumb.category === 'console') return null;
  // A breadcrumb's data is a request's or a log's fields; only a request's path is kept.
  const { data, message, ...rest } = crumb;
  const url = data?.['url'];
  return {
    ...rest,
    ...(message ? { message: scrubText(message) } : {}),
    ...(url ? { data: { url: scrubText(String(url)).split('?')[0] } } : {}),
  };
};

/**
 * Turns on error tracking when the server has a DSN for the website (GET /client-config, from
 * WEB_SENTRY_DSN). The SDK is its own chunk, loaded only then, so a site without it, or the
 * landing page before it's known, pays nothing. Never throws: tracking is never the reason
 * the site doesn't open.
 */
export const startErrorTracking = async (
  load: () => Promise<typeof SentryReact> = () => import('@sentry/react'),
): Promise<boolean> => {
  try {
    const res = await fetch(`${env.apiBaseUrl}/client-config`);
    if (!res.ok) return false;
    const config = (await res.json()) as ClientConfigDto;
    if (!config.sentryDsn) return false;
    const Sentry = await load();
    Sentry.init({
      dsn: config.sentryDsn,
      environment: import.meta.env.MODE,
      ...(config.release ? { release: config.release } : {}),
      tracesSampleRate: 0,
      beforeSend: scrubEvent,
      beforeBreadcrumb: scrubBreadcrumb,
    });
    return true;
  } catch {
    return false;
  }
};
